import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(result === "success" ? "checking" : "cancel");
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (result !== "success" || !sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success"); setOrderNumber(data.order_number); setOrderId(data.order_id);
          // Fire GA4 purchase ONLY after backend-confirmed payment; idempotent per order.
          try {
            const { data: order } = await api.get(`/orders/${data.order_id}`);
            trackPurchase(order);
          } catch { trackPurchase({ order_number: data.order_number, id: data.order_id }); }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 12) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [result, sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      {state === "checking" && (<><Loader2 className="w-12 h-12 animate-spin text-amber mx-auto" /><h1 className="font-display text-2xl font-bold text-slateink mt-6">Confirming your payment…</h1><p className="text-slate-500 mt-2">Just a moment while we finalize your order.</p></>)}
      {state === "success" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-sage mx-auto" />
          <h1 className="font-display text-3xl font-bold text-slateink mt-6">Thank you! ✨</h1>
          <p className="text-slate-600 mt-3">We've received your order{orderNumber ? <> <span className="font-semibold">{orderNumber}</span></> : ""}. A confirmation email is on its way, and you can track everything from your dashboard.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold"><Link to="/account" data-testid="go-to-account">Track my order <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
            <Button asChild variant="outline" className="rounded-full font-semibold"><Link to="/shop">Keep shopping</Link></Button>
          </div>
        </>
      )}
      {(state === "cancel" || state === "failed") && (
        <>
          <XCircle className="w-16 h-16 text-rose mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slateink mt-6">{state === "cancel" ? "Payment canceled" : "Payment didn't go through"}</h1>
          <p className="text-slate-600 mt-3">No charge was made. Your order is saved — you can complete payment anytime from your account.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold"><Link to="/account">Go to my account</Link></Button>
            <Button asChild variant="outline" className="rounded-full font-semibold"><Link to="/shop">Back to shop</Link></Button>
          </div>
        </>
      )}
      {state === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 text-amber mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slateink mt-6">Still processing</h1>
          <p className="text-slate-600 mt-3">Your payment is taking a little longer than usual. Check your account in a moment — your order will appear once confirmed.</p>
          <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-6"><Link to="/account">Go to my account</Link></Button>
        </>
      )}
    </div>
  );
}
